import { useEffect } from 'react';

import { css } from '@compiled/react';

import { media } from '@atlaskit/primitives/responsive';

import {
	Assignee,
	EpicName,
	IssueTypeName,
	Priority,
} from '@/components/product-demos/animated-jira-tour/src/constants/ui.ts';
import { useAnimatedJiraBoardStore } from '@/components/product-demos/animated-jira-tour/src/state.ts';
import { wacToken } from '@/libraries/design/foundations';

import { BoardBody } from './board-body';
import { BoardHeader } from './board-header';
import { JiraIssue } from './constants/types';
import { ANIMATED_CARD_ID } from './constants/ui';

import type { AnimatedJiraTourProps } from './animated-jira-tour';

const rootContainerStyle = css({
	maxHeight: '550px',
	maxWidth: '800px',
	width: '100%',
	backgroundColor: wacToken('color.neutral.light0'),
	borderRadius: '10px',
	padding: '8px',
	overflow: 'hidden',
	display: 'flex',
	flexDirection: 'column',
	gap: '16px',
	boxShadow: `0px 18px 40px 0px #0000001A, 0px 73px 73px 0px #00000017, 0px 165px 99px 0px #0000000D, 0px 294px 118px 0px #00000003, 0px 459px 129px 0px #00000000`,
	[media.below.lg]: {
		maxWidth: '600px',
	},
});
const animationBoardStyles = css({
	'@keyframes jiraTourBoardFadeIn': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { opacity: 0 },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { opacity: 1 },
	},
	'@keyframes jiraTourBoardGrow': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { maxHeight: '0px' },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { maxHeight: '550px' },
	},
	'@keyframes jiraTourBoardShrink': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { maxHeight: '550px' },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { maxHeight: '0px' },
	},
	'@keyframes jiraTourBoardFadeOut': {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		from: { opacity: 1 },
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
		to: { opacity: 0 },
	},
	animation:
		'jiraTourBoardFadeIn 0.5s forwards, jiraTourBoardGrow 1s forwards, jiraTourBoardShrink 1s forwards 11.75s, jiraTourBoardFadeOut 0.5s forwards 11.75s',
});

type JswAnimatedBoardProps = Pick<
	AnimatedJiraTourProps,
	'displayMode' | 'layoutMode' | 'animatedJiraCardOverride'
> & {
	inProgressCardsOverride?: Array<JiraIssue>;
	doneCardsOverride?: Array<JiraIssue>;
};

export const JswAnimatedBoard = (props: JswAnimatedBoardProps) => {
	const [{ messages }, { setInProgressCol, setDoneCol }] = useAnimatedJiraBoardStore();

	useEffect(() => {
		void setInProgressCol(
			props.inProgressCardsOverride ?? [
				{
					id: 'inProg1',
					summary: messages.inProgStaticLabel1,
					issueType: IssueTypeName.STORY,
					epicName: EpicName.FEATURES,
					assignee: Assignee.JANE,
					priority: Priority.BLOCKER,
				},
				{
					id: 'inProg2',
					summary: messages.inProgStaticLabel2,
					issueType: IssueTypeName.STORY,
					epicName: EpicName.ANALYTICS,
					assignee: Assignee.BLAKE,
					priority: Priority.HIGH,
				},
			],
		);
		const doneCards = props.doneCardsOverride ?? [
			{
				id: 'done1',
				summary: messages.doneStaticLabel1,
				issueType: IssueTypeName.BUG,
				epicName: EpicName.PAYMENTS,
				assignee: Assignee.ALVIN,
				priority: Priority.MINOR,
			},
			{
				id: 'done2',
				summary: messages.doneStaticLabel2,
				issueType: IssueTypeName.TASK,
				epicName: EpicName.FEATURES,
				assignee: Assignee.JANE,
				priority: Priority.CRITICAL,
			},
		];
		void setDoneCol(
			props.displayMode === 'static'
				? [
						...doneCards,
						{
							summary: messages.animatedCardHeading,
							issueType: IssueTypeName.STORY,
							epicName: EpicName.FEATURES,
							assignee: Assignee.ALVIN,
							priority: Priority.HIGH,
							...props.animatedJiraCardOverride,
							id: ANIMATED_CARD_ID,
						},
					]
				: doneCards,
		);
	}, [
		messages,
		props.inProgressCardsOverride,
		props.doneCardsOverride,
		props.animatedJiraCardOverride,
		props.displayMode,
		setInProgressCol,
		setDoneCol,
	]);

	return (
		<div
			css={[
				rootContainerStyle,
				props.displayMode !== 'static' && animationBoardStyles,
				props.layoutMode === 'fixed-scene' &&
					css({ height: '100%', [media.below.lg]: { maxWidth: '800px' } }),
			]}
		>
			<BoardHeader layoutMode={props.layoutMode} />
			<BoardBody layoutMode={props.layoutMode} displayMode={props.displayMode} />
		</div>
	);
};

export default JswAnimatedBoard;
